import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { formatCPFOrCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';
import { PsaAuthorizationService } from 'src/app/services/auth/psa-authorization.service';
import {
  BiomesRercord,
  Developer,
  Locations,
  PsaProjectData,
  ProjectStatus,
  ProjectStatusCode,
  ProjectUpdateStatus,
  getProjectStatusDisplayLabel,
  getProjectStatusIcon,
} from 'src/app/shared/interfaces/psa-project-register';
import { ProjectBaselineActivityService } from '../../../services/project-baseline-activity.service';
import { ProjectMethodologiesService } from '../../../services/project-methodology.service';
import { ProjectService } from '../../../services/project.service';
import { AppToastService } from 'src/app/shared/services/app-toast.service';

export interface PropertyLocationView {
  ownerName: string;
  ownerDocument: string;
  municipality: string;
  state: string;
  coordinates: string;
  totalHectares: string;
  carbonHectares: string;
  biome: string;
  car: string;
}

@Component({
  standalone: false,
  selector: 'app-project-consult-details-drawer',
  templateUrl: './project-consult-details-drawer.component.html',
  styleUrls: ['./project-consult-details-drawer.component.scss'],
})
export class ProjectConsultDetailsDrawerComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() projectId: number | null = null;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() projectUpdated = new EventEmitter<void>();
  @Output() rmConfirmed = new EventEmitter<{ projectId: number; projectName: string }>();
  @Output() detailsExported = new EventEmitter<void>();

  projectData?: PsaProjectData;
  developerPrincipal?: Developer;
  otherDevelopers: Developer[] = [];
  propertyLocations: PropertyLocationView[] = [];
  baselineActivity = '';
  methodologyLabel = '-';
  isLoading = false;
  showConfirmRmModal = false;

  private hasRegisterPermission = false;
  private loadSubscription?: Subscription;
  private updateSubscription?: Subscription;

  constructor(
    private readonly projectService: ProjectService,
    private readonly projectBaselineActivityService: ProjectBaselineActivityService,
    private readonly projectMethodologiesService: ProjectMethodologiesService,
    private readonly psaAuthorizationService: PsaAuthorizationService,
    private readonly datePipe: DatePipe,
    private readonly toastService: AppToastService,
  ) {}

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.updateSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['open'] || changes['projectId']) && this.open && this.projectId != null) {
      this.loadPermissions();
      this.loadProjectDetails(this.projectId);
    }

    if (changes['open'] && !this.open) {
      this.showConfirmRmModal = false;
    }
  }

  get statusTone(): 'info' | 'success' | 'danger' | 'warning' | 'default' {
    return this.resolveStatusTone(this.projectData?.status);
  }

  get statusLabel(): string {
    return getProjectStatusDisplayLabel(this.projectData?.status);
  }

  get statusIcon(): string {
    return getProjectStatusIcon(this.projectData?.status);
  }

  get showConfirmRmAction(): boolean {
    return this.hasRegisterPermission
      && this.projectData?.status === ProjectStatus.PROJECT_PENDING_DEVELOPER_CONFIRMATION;
  }

  get otherDevelopersExportLabel(): string {
    if (!this.otherDevelopers.length) {
      return '';
    }

    return this.otherDevelopers
      .map(developer => `${developer.developerName || '-'}\t${developer.cpfCnpj || '-'}`)
      .join('\n');
  }

  get propertiesExportLabel(): string {
    if (!this.propertyLocations.length) {
      return '';
    }

    return this.propertyLocations
      .map((property, index) => {
        const prefix = this.propertyLocations.length > 1 ? `Propriedade ${index + 1}\n` : '';
        return `${prefix}${property.ownerName}\t${property.ownerDocument}\n`
          + `${property.municipality}\t${property.state}\n`
          + `${property.coordinates}\t${property.totalHectares}\n`
          + `${property.carbonHectares}\t${property.biome}\n`
          + `${property.car}`;
      })
      .join('\n\n');
  }

  get developerPhoneLabel(): string {
    return this.formatPhone(this.developerPrincipal?.phone);
  }

  get documentLinks(): string[] {
    return (this.projectData?.attachments ?? [])
      .map(attachment => attachment.link)
      .filter((link): link is string => !!link?.trim());
  }

  get selectedOdsTags(): string[] {
    return (this.projectData?.odsNumbers ?? [])
      .filter(odsNumber => Number.isInteger(odsNumber))
      .map(odsNumber => `ODS ${odsNumber}`);
  }

  get selectedOdsCountLabel(): string {
    const count = this.selectedOdsTags.length;

    if (!count) {
      return 'ODS selecionados';
    }

    return count === 1 ? '1 ODS selecionado' : `${count} ODS selecionados`;
  }

  close(): void {
    this.open = false;
    this.openChange.emit(false);
    this.showConfirmRmModal = false;
  }

  openConfirmRmModal(): void {
    this.showConfirmRmModal = true;
  }

  onConfirmRmModalDismiss(): void {
    this.showConfirmRmModal = false;
    this.releaseOpenDialogs();
  }

  onConfirmRmModalConfirmed(): void {
    this.showConfirmRmModal = false;
    this.confirmRmReceipt();
  }

  exportDetails(): void {
    if (!this.projectData) {
      return;
    }

    const rows = [
      ['Campo', 'Valor'],
      ['Nome do projeto', this.projectData.projectName ?? ''],
      ['Conta registrador', this.projectData.registerCode ?? ''],
      ['Data de início', this.projectData.projectStartDate ?? ''],
      ['Atividade de linha de base', this.baselineActivity],
      ['Descrição', this.projectData.generalDetails ?? ''],
      ['Desenvolvedor', this.developerPrincipal?.developerName ?? ''],
      ['CPF/CNPJ', this.developerPrincipal?.cpfCnpj ?? ''],
      ['E-mail', this.developerPrincipal?.email ?? ''],
      ['Telefone', this.developerPhoneLabel !== '-' ? this.developerPhoneLabel : ''],
      ['Outros desenvolvedores', this.otherDevelopersExportLabel],
      ['Propriedades', this.propertiesExportLabel],
      ['Metodologia', this.methodologyLabel !== '-' ? this.methodologyLabel : ''],
      ['Link relatório', this.projectData.methodologyReportLink ?? ''],
      ['Descrição metodologia', this.projectData.methodologyDocumentDescription ?? ''],
      ['Documentos', this.documentLinks.join('\n')],
      ['ODS selecionados', this.selectedOdsTags.join(', ')],
      ['Outros co-benefícios', this.projectData.coBenefits ?? ''],
      ['Status', this.projectData.status ?? ''],
    ];

    const csv = rows
      .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `projeto-${this.projectData.projectId ?? 'detalhes'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    this.detailsExported.emit();
    this.toastService.show({
      variant: 'success',
      label: 'Arquivo com detalhes exportado com sucesso!',
    });
  }

  private loadPermissions(): void {
    const psaFunctions = this.psaAuthorizationService.getFunctions();
    this.hasRegisterPermission = psaFunctions.some(
      (f: { functionCode?: string }) => f.functionCode === 'CAR_PSA_REGISTRA_PROJETO',
    );
  }

  private confirmRmReceipt(): void {
    if (this.projectId == null) {
      return;
    }

    const data: ProjectUpdateStatus = {
      status: ProjectStatusCode.PROJECT_REGISTERED_IN_B3,
      description: '',
    };

    const projectId = this.projectId;
    const projectName = this.projectData?.projectName?.trim() ?? '';

    this.updateSubscription?.unsubscribe();
    this.updateSubscription = this.projectService.updateProjectStatus(projectId, data).subscribe({
      next: () => {
        this.projectUpdated.emit();
        this.rmConfirmed.emit({ projectId, projectName });
        this.close();
      },
      error: error => {
        console.error('Erro ao confirmar o recebimento do RM.', error);
      },
    });
  }

  private loadProjectDetails(projectId: number): void {
    this.isLoading = true;
    this.loadSubscription?.unsubscribe();

    this.loadSubscription = this.projectService.getProjectById(projectId).subscribe({
      next: response => {
        const project = response?.data;
        if (!project) {
          this.isLoading = false;
          return;
        }

        this.projectData = this.projectService.parseProjectStatus(project);
        this.projectData.projectStartDate = this.formatDate(this.projectData.projectStartDate);

        this.developerPrincipal = this.projectData.developers?.find(developer => developer.principal);
        this.otherDevelopers = (this.projectData.developers ?? [])
          .filter(developer => !developer.principal)
          .map(developer => ({
            ...developer,
            cpfCnpj: developer.cpfCnpj
              ? formatCPFOrCNPJ(developer.cpfCnpj) ?? developer.cpfCnpj
              : developer.cpfCnpj,
          }));

        if (this.developerPrincipal?.cpfCnpj) {
          this.developerPrincipal.cpfCnpj = formatCPFOrCNPJ(this.developerPrincipal.cpfCnpj) ?? this.developerPrincipal.cpfCnpj;
        }

        this.propertyLocations = this.mapPropertyLocations(this.projectData.locations ?? []);

        this.loadBaselineActivity(this.projectData.baselineActivityId);
        this.loadMethodology(this.projectData.methodologyId);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private loadBaselineActivity(baselineActivityId?: number | null): void {
    if (baselineActivityId == null) {
      this.baselineActivity = '-';
      return;
    }

    this.projectBaselineActivityService.getBaselinesActivity().subscribe(response => {
      const baselines = response?.data ?? [];
      const match = baselines.find(
        (item: { baselineActivityIdentifier: number }) => item.baselineActivityIdentifier === baselineActivityId,
      );
      this.baselineActivity = match?.baselineActivityDescription ?? '-';
    });
  }

  private loadMethodology(methodologyId?: number | null): void {
    if (methodologyId == null) {
      this.methodologyLabel = '-';
      return;
    }

    this.projectMethodologiesService.getMethodologies().subscribe(response => {
      const methodologies = response?.data ?? [];
      const match = methodologies.find(
        (item: { methodologyIdentifier: number }) => item.methodologyIdentifier === methodologyId,
      );
      this.methodologyLabel = match?.methodologyDescription ?? '-';
    });
  }

  private formatPhone(phone?: string | null): string {
    if (!phone?.trim()) {
      return '-';
    }

    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      return phone.trim();
    }

    return `(${digits.slice(0, 2)})${digits.slice(2)}`;
  }

  private formatDate(value?: string | null): string {
    if (!value?.trim()) {
      return '-';
    }

    const trimmed = value.trim();
    const isoDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      return `${day}/${month}/${year}`;
    }

    const parsed = new Date(trimmed);
    if (isNaN(parsed.getTime())) {
      return trimmed;
    }

    return this.datePipe.transform(parsed, 'dd/MM/yyyy') ?? trimmed;
  }

  private mapPropertyLocations(locations: Locations[]): PropertyLocationView[] {
    return locations.map(location => ({
      ownerName: location.landOwnerFullName?.trim() || '-',
      ownerDocument: location.landOwnerCpfCnpj
        ? formatCPFOrCNPJ(location.landOwnerCpfCnpj) ?? location.landOwnerCpfCnpj
        : '-',
      municipality: location.municipality?.trim() || '-',
      state: location.state?.trim() || '-',
      coordinates: this.formatCoordinates(location.latitude, location.longitude),
      totalHectares: this.formatHectares(location.totalHectares),
      carbonHectares: this.formatHectares(location.carbonGenerationHectares),
      biome: this.formatBiome(location.biome),
      car: location.registrationRecord?.trim() || '-',
    }));
  }

  private formatCoordinates(latitude?: string, longitude?: string): string {
    const lat = this.formatCoordinatePart(latitude);
    const lng = this.formatCoordinatePart(longitude);

    if (lat === '-' && lng === '-') {
      return '-';
    }

    return `Lat ${lat} / Long ${lng}`;
  }

  private formatCoordinatePart(value?: string): string {
    if (!value?.trim()) {
      return '-';
    }

    const trimmed = value.trim();
    const directionMatch = trimmed.match(/\s*([A-Za-z])$/);
    const direction = directionMatch ? directionMatch[1].toUpperCase() : '';
    const numericPart = directionMatch
      ? trimmed.slice(0, directionMatch.index).trim()
      : trimmed;
    const isNegative = numericPart.startsWith('-');
    const digits = numericPart.replace(/\D/g, '');

    if (!digits) {
      return trimmed;
    }

    const grouped = digits.match(/.{1,2}/g)?.join('.') ?? digits;
    const formattedValue = isNegative ? `-${grouped}` : grouped;

    return direction ? `${formattedValue} ${direction}` : formattedValue;
  }

  private formatHectares(value?: number | null): string {
    if (value == null || Number.isNaN(value)) {
      return '-';
    }

    return `${new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)} ha`;
  }

  private formatBiome(biome?: string): string {
    if (!biome) {
      return '-';
    }

    const label = BiomesRercord[biome] ?? biome.replace(/_/g, ' ');

    return label.charAt(0) + label.slice(1).toLowerCase();
  }

  private resolveStatusTone(status?: string): 'info' | 'success' | 'danger' | 'warning' | 'default' {
    switch (status) {
      case ProjectStatus.PROJECT_REJECTED_BY_AUDITOR:
      case ProjectStatus.PROJECT_REJECTED_BY_DEVELOPER:
        return 'danger';
      case ProjectStatus.PROJECT_APPROVED_BY_AUDITOR:
      case ProjectStatus.PROJECT_CONFIRMED_BY_DEVELOPER:
      case ProjectStatus.PROJECT_REGISTERED_IN_B3:
        return 'success';
      case ProjectStatus.PROJECT_DRAFT:
      case ProjectStatus.PROJECT_PENDING_AUDITOR_VALIDATION:
        return 'warning';
      case ProjectStatus.PROJECT_PENDING_DEVELOPER_CONFIRMATION:
        return 'info';
      default:
        return 'info';
    }
  }

  private releaseOpenDialogs(): void {
    document.querySelectorAll('dialog[open]').forEach(element => {
      if (element instanceof HTMLDialogElement) {
        element.close();
      }
    });
  }
}
